import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { isHttpsUrl, type OAuth2FormData } from '../form/customIntegrationForm'
import { IdentityFormFields } from './IdentityFormFields'
import { ScopesTagInput } from './ScopesTagInput'

interface OAuth2FormFieldsProps {
  data: OAuth2FormData
  onChange: (data: OAuth2FormData) => void
  disabled?: boolean
  hasStoredSecret?: boolean
}

export const OAuth2FormFields: React.FC<OAuth2FormFieldsProps> = ({ data, onChange, disabled = false, hasStoredSecret = false }) => {
  const [optionalOpen, setOptionalOpen] = useState<boolean>(true)
  const update = (patch: Partial<OAuth2FormData>) => onChange({ ...data, ...patch })
  const authorizationUrl = data.authorizationUrl.trim()
  const tokenUrl = data.tokenUrl.trim()

  return (
    <div className='d-flex flex-column gap-3'>
      <Form.Group controlId='custom-integration-client-id'>
        <Form.Label className='paragraph-small mb-1'>Client ID</Form.Label>
        <Form.Control
          type='text'
          placeholder='Enter client ID'
          value={data.clientId}
          disabled={disabled}
          onChange={(e) => update({ clientId: e.target.value })}
        />
      </Form.Group>

      <Form.Group controlId='custom-integration-client-secret'>
        <Form.Label className='paragraph-small mb-1'>Client Secret</Form.Label>
        <Form.Control
          type='password'
          autoComplete='off'
          placeholder={hasStoredSecret ? 'Leave empty to keep the current secret' : 'Enter client secret'}
          value={data.clientSecret}
          disabled={disabled}
          onChange={(e) => update({ clientSecret: e.target.value })}
        />
      </Form.Group>

      <Form.Group controlId='custom-integration-authorization-url'>
        <Form.Label className='paragraph-small mb-1'>Authorization URL</Form.Label>
        <Form.Control
          type='url'
          placeholder='e.g. https://acme.xyz/oauth/authorize'
          value={data.authorizationUrl}
          disabled={disabled}
          isInvalid={authorizationUrl !== '' && !isHttpsUrl(authorizationUrl)}
          onChange={(e) => update({ authorizationUrl: e.target.value })}
        />
        <Form.Control.Feedback type='invalid'>Please enter a valid HTTPS URL.</Form.Control.Feedback>
        <Form.Text className='paragraph-extra-small zocks-text-gray-600'>URL to initiate the OAuth handshake and show consent screen.</Form.Text>
      </Form.Group>

      <Form.Group controlId='custom-integration-token-url'>
        <Form.Label className='paragraph-small mb-1'>Token request URL</Form.Label>
        <Form.Control
          type='url'
          placeholder='e.g. https://acme.xyz/oauth/token'
          value={data.tokenUrl}
          disabled={disabled}
          isInvalid={tokenUrl !== '' && !isHttpsUrl(tokenUrl)}
          onChange={(e) => update({ tokenUrl: e.target.value })}
        />
        <Form.Control.Feedback type='invalid'>Please enter a valid HTTPS URL.</Form.Control.Feedback>
        <Form.Text className='paragraph-extra-small zocks-text-gray-600'>URL to exchange the authorization code for access token</Form.Text>
      </Form.Group>

      <div className='d-flex align-items-center justify-content-between'>
        <span className='paragraph-medium fw-bold zocks-text-gray-900'>Optional Preferences</span>
        <Button
          type='button'
          variant='primaryLinkButton'
          className='d-flex align-items-center gap-1 text-decoration-none'
          onClick={() => setOptionalOpen((open) => !open)}
        >
          {optionalOpen ? 'Hide' : 'Show'}
          <i className={optionalOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} aria-hidden='true' />
        </Button>
      </div>

      {optionalOpen ? (
        <div className='d-flex flex-column gap-3'>
          <Form.Check id='custom-integration-use-basic-auth' type='checkbox' disabled={disabled}>
            <Form.Check.Input type='checkbox' checked={data.useBasicAuth} disabled={disabled} onChange={(e) => update({ useBasicAuth: e.target.checked })} />
            <Form.Check.Label className='d-flex flex-column'>
              <span className='paragraph-medium zocks-text-gray-900'>Use HTTP Basic Authentication</span>
              <span className='paragraph-small zocks-text-gray-600'>Send client credentials via the HTTP Basic Auth header instead of the POST body.</span>
            </Form.Check.Label>
          </Form.Check>

          <Form.Check id='custom-integration-pkce' type='checkbox' disabled={disabled}>
            <Form.Check.Input type='checkbox' checked={data.pkceEnabled} disabled={disabled} onChange={(e) => update({ pkceEnabled: e.target.checked })} />
            <Form.Check.Label className='d-flex flex-column'>
              <span className='paragraph-medium zocks-text-gray-900'>Use PKCE (Proof Key for Code Exchange)</span>
              <span className='paragraph-small zocks-text-gray-600'>Enable PKCE for enhanced security during the authorization code flow.</span>
            </Form.Check.Label>
          </Form.Check>

          <ScopesTagInput scopes={data.scopes} onChange={(scopes) => update({ scopes })} disabled={disabled} />

          <IdentityFormFields identity={data.identity} onChange={(identity) => update({ identity })} disabled={disabled} />
        </div>
      ) : null}
    </div>
  )
}
